<script setup lang="ts">
import { computed } from 'vue'
import { formatNumber } from '@/utils/number'
import { optimizeAvatarUrl } from '@/utils/distribute/media-url'
import DreamaProgressiveImage from '@/components/dreama-progressive-image'
import type { Props } from './type'

defineOptions({
  name: 'ProfileCartoonCard',
})

const props = defineProps<Props>()
const emit = defineEmits<{
  click: [item: Props['item']]
}>()

const banner = computed(() => props.item.banner || props.item.avatar)
const avatar = computed(() => optimizeAvatarUrl(props.item.avatar) ?? props.item.avatar)
const isPublished = computed(() => props.item.status === 11)
const summaryText = computed(() => props.item.summary?.trim() || '这个角色还没有简介')
const stats = computed(() => [
  `${formatNumber(props.item.useNum ?? 0)}互动与播放`,
  `${formatNumber(props.item.fansNum ?? 0)}粉丝`,
])

function handleClick() {
  emit('click', props.item)
}
</script>

<template>
  <article
    class="cartoon-card"
    role="button"
    tabindex="0"
    :aria-label="item.name"
    @click="handleClick"
    @keydown.enter.prevent="handleClick"
    @keydown.space.prevent="handleClick"
  >
    <DreamaProgressiveImage
      class="cartoon-card__cover"
      :src="banner"
      :alt="item.name"
      aspect-ratio="3 / 4"
      :preview-width="48"
    >
      <div class="cartoon-card__cover-shade" />
      <div class="cartoon-card__content">
        <div class="cartoon-card__identity">
          <img class="cartoon-card__avatar" :src="avatar" :alt="item.name" />
          <h3 class="cartoon-card__name">{{ item.name }}</h3>
        </div>
        <p class="cartoon-card__summary">{{ summaryText }}</p>
        <div v-if="isPublished" class="cartoon-card__stats">
          <span v-for="stat in stats" :key="stat">{{ stat }}</span>
        </div>
        <p v-else class="cartoon-card__draft-hint">角色发布后查看数据</p>
      </div>
    </DreamaProgressiveImage>
  </article>
</template>

<style scoped>
.cartoon-card {
  position: relative;
  display: inline-flex;
  width: 100%;
  contain: layout paint style;
  content-visibility: auto;
  contain-intrinsic-size: auto 340px;
  cursor: pointer;
  flex-direction: column;
  padding: 12px;
  color: var(--profile-card-text);
  outline: none;
  border-radius: 16px;
}

.cartoon-card__cover {
  position: relative;
  overflow: hidden;
  border-radius: 12px;
  background: var(--profile-image-bg);
  --dreama-image-placeholder-bg: var(--profile-image-bg);
}

.cartoon-card__cover-shade {
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgb(0 0 0 / 0) 30%, rgb(0 0 0 / 0.9) 100%);
}

.cartoon-card__content {
  position: absolute;
  inset: auto 0 0;
  display: flex;
  min-height: 0;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  color: var(--profile-on-cover);
}

.cartoon-card__identity {
  display: inline-flex;
  min-width: 0;
  align-items: center;
  gap: 8px;
}

.cartoon-card__avatar {
  display: block;
  width: 40px;
  height: 40px;
  flex: 0 0 auto;
  border-radius: 50%;
  object-fit: cover;
  background: transparent;
}

.cartoon-card__name {
  overflow: hidden;
  margin: 0;
  color: var(--profile-on-cover);
  font-size: 16px;
  font-weight: 600;
  line-height: 22px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cartoon-card__summary {
  overflow: hidden;
  margin: 0;
  color: rgb(255 255 255 / 0.92);
  font-size: 12px;
  line-height: 17px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cartoon-card__stats {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 16px;
  color: rgb(255 255 255 / 0.88);
  font-size: 12px;
  line-height: 16px;
}

.cartoon-card__draft-hint {
  margin: 0;
  color: rgb(255 255 255 / 0.3);
  font-size: 12px;
  line-height: 16px;
}

.cartoon-card::before {
  position: absolute;
  inset: 4px;
  z-index: -1;
  border-radius: 20px;
  background-color: rgb(0 0 0 / 0.045);
  content: '';
  opacity: 0;
  transform: scale(var(--app-interaction-media-rest-scale));
  transition:
    opacity 0.18s ease,
    transform 0.18s ease;
}

.cartoon-card:hover::before,
.cartoon-card:focus-visible::before {
  opacity: 1;
  transform: scale(var(--app-interaction-media-reveal-scale));
}

@media (max-width: 640px) {
  .cartoon-card {
    padding: 7px 5px 14px;
  }

  .cartoon-card__cover {
    border-radius: 10px;
  }

  .cartoon-card__content {
    gap: 10px;
    padding: 14px 12px;
  }

  .cartoon-card__name {
    font-size: 15px;
    line-height: 21px;
  }

  .cartoon-card__stats {
    gap: 12px;
  }
}
</style>
